<template>
  <button
    class="app-btn"
    :class="classes"
    :disabled="disabled || loading"
    type="button"
    v-on="$listeners"
  >
    <span v-if="loading" class="app-btn__spinner" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-dasharray="42" stroke-dashoffset="12" />
      </svg>
    </span>
    <v-icon v-if="prependIcon && !loading" class="app-btn__icon" :size="iconSize">{{ prependIcon }}</v-icon>
    <slot />
  </button>
</template>

<script>
export default {
  name: 'Button',
  inheritAttrs: false,
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'outlined', 'ghost', 'danger'].includes(v),
    },
    size: {
      type: String,
      default: 'default',
      validator: v => ['small', 'default', 'large'].includes(v),
    },
    block:       { type: Boolean, default: false },
    loading:     { type: Boolean, default: false },
    disabled:    { type: Boolean, default: false },
    prependIcon: { type: String,  default: null },
  },
  computed: {
    classes () {
      return [
        `app-btn--${this.variant}`,
        `app-btn--${this.size}`,
        { 'app-btn--block':    this.block },
        { 'app-btn--loading':  this.loading },
        { 'app-btn--disabled': this.disabled || this.loading },
      ]
    },
    iconSize () {
      return this.size === 'small' ? 14 : this.size === 'large' ? 20 : 16
    },
  },
}
</script>

<style lang="scss" scoped>
$red   : #EF2929;
$grey  : #7C7C8A;
$white : #FFFFFF;

.app-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 2px solid transparent;
  border-radius: 8px;
  font-family: inherit;
  font-weight: 600;
  letter-spacing: 0.4px;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.1s ease, background 0.15s ease, border-color 0.15s ease;
  outline: none;
  white-space: nowrap;

  &:active:not(:disabled) { transform: scale(0.98); }

  /* Sizes */
  &--small   { padding: 6px 14px;  font-size: 13px; min-height: 32px; }
  &--default { padding: 10px 20px; font-size: 14px; min-height: 40px; }
  &--large   { padding: 13px 24px; font-size: 15px; min-height: 48px; }
  &--block   { width: 100%; }

  /* Variants */
  &--primary {
    background: var(--v-primary-base);
    color: $white;
    border-color: var(--v-primary-base);
    &:hover:not(:disabled) { background: var(--v-primary-darken1); border-color: var(--v-primary-darken1); }
  }

  &--outlined {
    background: transparent;
    color: var(--v-primary-base);
    border-color: var(--v-primary-base);
    &:hover:not(:disabled) { background: var(--v-primary-lighten5); }
  }

  &--ghost {
    background: transparent;
    color: $grey;
    border-color: transparent;
    &:hover:not(:disabled) { color: $white; background: rgba(255, 255, 255, 0.06); }
  }

  &--danger {
    background: $red;
    color: $white;
    border-color: $red;
    &:hover:not(:disabled) { background: darken($red, 8%); border-color: darken($red, 8%); }
  }

  /* States */
  &--disabled,
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    pointer-events: none;
  }

  &--loading { cursor: default; pointer-events: none; }

  /* Spinner */
  &__spinner {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1em;
    height: 1em;
    flex-shrink: 0;

    svg { width: 100%; height: 100%; animation: app-btn-spin 0.8s linear infinite; }
  }

  /* Icon */
  &__icon { color: inherit !important; flex-shrink: 0; }
}

@keyframes app-btn-spin {
  to { transform: rotate(360deg); }
}
</style>
